import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { IDashboardChartPallete } from '../dashboard-card/dashboard-card.service';

export interface IMiniChartConfig<T = string> {
  label: string;
  key: T;
  isCurrency: boolean;
  isPercentage?: boolean;
}

@Component({
  selector: 'app-dashboard-card-summary',
  templateUrl: './dashboard-card-summary.component.html',
  styleUrls: ['./dashboard-card-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardCardSummaryComponent implements OnInit {
  @Input()
  public series: any;

  @Input()
  public statsTotalData: any;

  @Input()
  public selectedCharts: number[] = [];

  @Input()
  public selectedMetrics: string[] = [];

  @Input()
  public excludedMetrics: string[] | null = [];

  @Input()
  public selectedChartsPalletes: Map<number, IDashboardChartPallete> | null = new Map();

  @Output()
  public selectChart: EventEmitter<number> = new EventEmitter();

  @Output()
  public selectMetrics: EventEmitter<string[]> = new EventEmitter();

  public smallChartStatsMap: Record<string, IMiniChartConfig>;
  public readonly secondLineStartIndex = 3;
  public readonly secondLineEndIndex = 5;

  private _smallChartStats: IMiniChartConfig[];

  public get smallChartStats(): IMiniChartConfig[] {
    return this._smallChartStats;
  }

  @Input()
  public set smallChartStats(smallChartStats: IMiniChartConfig[]) {
    this._smallChartStats = smallChartStats;
    this.smallChartStatsMap = smallChartStats.reduce(
      (acc, item) => ({
        ...acc,
        [item.key]: item,
      }),
      {}
    );
  }

  public ngOnInit(): void {
    setTimeout(() => {
      this.dispatchMiniChartsRedraw();
    }, 0);
  }

  public onSelectChart(chartIdx: number): void {
    const idx = Math.min(this.selectedMetrics.length - 1, chartIdx);
    const metric = this.selectedMetrics[idx];
    const areStatsNotEmpty = !!this.series?.[metric]?.values?.length;
    if (areStatsNotEmpty || this.isSelected(chartIdx)) {
      this.selectChart.emit(chartIdx);
    }
  }

  public onSelectMetric(idx: number, metric: string): void {
    this.selectedMetrics[idx] = metric;
    const newSelectedMetrics = [...this.selectedMetrics];
    newSelectedMetrics[idx] = metric;
    this.selectMetrics.emit(newSelectedMetrics);
  }

  public isSelected(chartIdx: number) {
    return this.selectedCharts?.indexOf(chartIdx) !== -1;
  }

  public getAvailableMetrics(): IMiniChartConfig[] {
    return this.smallChartStats
      .filter(({ key }) => !this.selectedMetrics.includes(key))
      .filter(({ key }) => !this.excludedMetrics?.includes(key));
  }

  public getChartPalette(chartIdx: number) {
    return this.selectedChartsPalletes?.get(chartIdx);
  }

  public getSafeLabel(metric: string): string {
    return this.smallChartStatsMap[metric]?.label;
  }

  private dispatchMiniChartsRedraw(): void {
    window.dispatchEvent(new Event('resize'));
  }
}
